import { useTaskWithErrorHandling } from "@/hooks/useTaskWithErrorHandling";
import { findContractByToken } from "@/Api/PublicRequestApi";
import { AppAlert } from "@/Components/AppAlert";
import Section from "@/Components/MagicLink/Section";
import WarningIcon from "@/Components/MagicLink/WarningIcon";
import { usePublicRequestProvider } from "@/Provider/PublicRequestProvider";
import { buildPublicSearchPath } from "@/Router/publicRoutes";
import DetailView from "@/Views/PublicRequest/DetailView";
import { PublicRequestSuccessContent } from "@/Views/PublicRequest/SuccessView";
import {
    magicLinkCenteredHeroInnerSx,
    magicLinkPageColumnSx,
    publicRequestContentSx,
} from "@/Utils/MagicLink/pageLayout";
import SearchIcon from "@mui/icons-material/Search";
import { Box, Button, CircularProgress, Link, Typography, useTheme } from "@mui/material";
import { SxProps, Theme } from "@mui/material/styles";
import { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { RequestMasterTypeEnum, RequestMasterTypeEnumSchema } from "@/Schemas/PublicRequest/RequestMasterTypeEnum";
import { getCallcentrumTelNumber, getCallcentrumOpenningHoursFrom, getCallcentrumOpenningHoursTo } from "../../Utils/environment";

const tokenViewContentSx: SxProps<Theme> = {
    ...publicRequestContentSx,
    paddingX: { xs: "16px", md: 2 },
    paddingY: { xs: "24px", md: "80px" },
    maxWidth: "100%",
    minWidth: 0,
    overflowX: "hidden",
};

const TokenView = () => {
    const { token: urlToken } = useParams<{ token: string }>();
    const { contractData, tokenStep, setContractData, getRecaptchaCode, requestMasterType, setRequestMasterType } =
        usePublicRequestProvider();

    const hasMatchingContractData =
        Boolean(urlToken) && contractData?.token === urlToken;

    const [loadFailed, setLoadFailed] = useState(() => !urlToken);
    const [isLoading, setIsLoading] = useState(
        () => Boolean(urlToken) && contractData?.token !== urlToken
    );

    const trySetRequestMasterType = (errors?: Record<string, string> | null): RequestMasterTypeEnum | null => {
        if (requestMasterType != null) return requestMasterType;
        const value = errors?.["requestMasterTypeId"];
        if (value == null) return null;
        const parsedValue = Number(value);
        if (!Number.isInteger(parsedValue)) return null;
        const result = RequestMasterTypeEnumSchema.safeParse(parsedValue);
        if (result.success) {
            setRequestMasterType(result.data);
            return result.data;
        }
        return null;
    };

    const cancelledRef = useRef(false);

    const [contractErrorOccured, setContractErrorOccured] = useState(() => false);
    const [genericErrorMessage, setGenericErrorMessage] = useState(() => "");

    const validateTokenTask = useTaskWithErrorHandling({
        asyncFunction: async () => {
            const recaptchaResponse = await getRecaptchaCode();
            const data = await findContractByToken({ token: urlToken ?? "", recaptcha: recaptchaResponse});
            if (!cancelledRef.current) {
                setContractData(data);
                setIsLoading(false);
            }
        },
        onError: (message, errorCode, errors) => {
            if (!cancelledRef.current) {
                setContractData(null);
                let rmt = trySetRequestMasterType(errors);
                setContractErrorOccured(message === "PUBLIC_REQUEST_INVALID" && rmt != null)
                setGenericErrorMessage(message !== "PUBLIC_REQUEST_INVALID"
                    ? message
                    : "Zmluva sa nenašla alebo požadovaný úkon nie je dostupný. Skúste to prosím neskôr."
                )
                setLoadFailed(true);
            }
        },
        genericErrorMessage: "Nastala chyba počas spracovania požiadavky. Skúste to prosím neskôr.",
    });

    useEffect(() => {
        if (contractData?.token === urlToken) {
            return;
        }

        validateTokenTask.perform();

        return () => {
            cancelledRef.current = true;
        };
    }, []);

    if (loadFailed) {
        return <PublicRequestNotFound
            contractErrorOccured={contractErrorOccured}
            genericErrorMessage={genericErrorMessage}
        />;
    }

    if (isLoading || !hasMatchingContractData) {
        return (
            <Box component="main" sx={tokenViewContentSx}>
                <CircularProgress color="primary" />
            </Box>
        );
    }

    if (tokenStep === "success") {
        return <PublicRequestSuccessContent />;
    }

    return <DetailView />;
};

export default TokenView;

type PublicRequestNotFoundProps = {
    contractErrorOccured?: boolean;
    genericErrorMessage?: string;
};

const PublicRequestNotFound = ({
    contractErrorOccured,
    genericErrorMessage,
}: PublicRequestNotFoundProps) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const { masterTypeConfig, requestMasterType } = usePublicRequestProvider();
    const notFoundMessage = masterTypeConfig?.invalidContractOnTokenView;

    const handleSearchContract = () => {
        if (requestMasterType !== null) {
            navigate(buildPublicSearchPath(String(requestMasterType)));
        }
    };

    return (
        <Box component="main" sx={tokenViewContentSx}>
            <Box
                sx={{
                    ...magicLinkPageColumnSx,
                    maxWidth: { xs: "100%", md: "620px" },
                    minWidth: 0,
                    width: "100%",
                }}
            >
                <Box
                    sx={{
                        ...magicLinkCenteredHeroInnerSx,
                        paddingY: 0,
                        justifyContent: "flex-start",
                        minWidth: 0,
                        width: "100%",
                    }}
                >
                    <Section
                        disableBodyPadding
                        disableBodyGap
                        sx={{
                            minWidth: { xs: 0, md: 682 },
                            maxWidth: "100%",
                            width: "100%",
                            boxSizing: "border-box",
                            overflow: "hidden",
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "center",
                                padding: { xs: "24px", md: "64px" },
                                boxSizing: "border-box",
                                minWidth: 0,
                                width: "100%",
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    justifyContent: "flex-start",
                                    textAlign: "center",
                                    gap: "32px",
                                    width: "100%",
                                    minWidth: 0,
                                    maxWidth: { md: 446 },
                                }}
                            >
                                <WarningIcon height={60} width={64} />
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        gap: "12px",
                                        width: "100%",
                                        minWidth: 0,
                                        maxWidth: { md: 350 },
                                    }}
                                >
                                    {contractErrorOccured && notFoundMessage ? (
                                        <>
                                            <Typography
                                                variant="h5"
                                                component="h1"
                                                color={theme.palette.error.dark}
                                                sx={{ overflowWrap: "anywhere" }}
                                            >
                                                {notFoundMessage.headline}
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{ overflowWrap: "anywhere" }}
                                            >
                                                {notFoundMessage.description}
                                            </Typography>
                                        </>
                                    ) : (
                                        <>
                                            <Typography
                                                variant="h5"
                                                component="h1"
                                                color={theme.palette.error.dark}
                                                sx={{ overflowWrap: "anywhere" }}
                                            >
                                                {genericErrorMessage}
                                            </Typography>
                                        </>
                                    )}
                                </Box>
                                {contractErrorOccured && requestMasterType != null && (
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        startIcon={<SearchIcon />}
                                        onClick={handleSearchContract}
                                        sx={{
                                            width: "100%",
                                            maxWidth: "100%",
                                            height: 48,
                                            minHeight: 48,
                                        }}
                                    >
                                        Vyhľadať zmluvu
                                    </Button>
                                )}
                                <Box sx={{ width: "100%", minWidth: 0, maxWidth: "100%" }}>
                                    <AppAlert
                                        headline="Potrebujete sa poradiť?"
                                        description={
                                            <>
                                                <span>
                                                    Kontaktujte našich kolegov z oddelenia zákazníckej podpory na
                                                    čísle{" "}
                                                </span>
                                                <Link
                                                    href={`tel:${getCallcentrumTelNumber().replaceAll(" ", "")}`}
                                                    sx={{
                                                        color: theme.palette.primary.main,
                                                        textDecoration: "underline",
                                                        fontWeight: 500,
                                                    }}
                                                >
                                                    {getCallcentrumTelNumber()}
                                                </Link>
                                                <span>
                                                    . Sú vám k dispozícii počas pracovných dní od {getCallcentrumOpenningHoursFrom()} do {getCallcentrumOpenningHoursTo()} hod.
                                                </span>
                                            </>
                                        }
                                    />
                                </Box>
                            </Box>
                        </Box>
                    </Section>
                </Box>
            </Box>
        </Box>
    );
};
